import { useTranslation } from 'react-i18next';
import { PolicyData } from '../../../context/policy';
import {
	ExposureCharacteristics,
	PerilCharacteristics,
} from '../../../types/policy';
import { useRouteMatch } from '../../../util/useRouteMatch';
import {
	findUpdatedExposureInContext,
	getActiveCharacteristics,
	getActiveExposuresForSidebar,
	getAddedExposureFromContext,
	getEntityNameFrom_DescriptionField,
	sortCharacteristicsByCoverageStartDate,
	sortCharacteristicsByStartDate,
	transformPerilCreateReqToPeril,
} from '../../../util/util';
import {
	TreeItemData,
	TreeItemInterface,
} from '../../layouts/SidePanel/TreeNode';
import {
	validateExposure,
	validatePeril,
	validatePolicyDetails,
} from '../fields/validations';
import { QuoteRoutes, QUOTE_ROUTES } from './QuoteSidebar';

export const QuotePolicyTree = (
	policyData: PolicyData,
	expandedItems: string[],
	currentErrorObject: any,
) => {
	const { t } = useTranslation();
	const {
		quoteUpdateObj,
		policyUpdateObj,
		productConfig,
		selectedQuote,
		policy,
	} = policyData;
	const updateObject = selectedQuote ? quoteUpdateObj : policyUpdateObj;
	const exposures = selectedQuote ? selectedQuote.exposures : policy.exposures;
	const addExposures = updateObject.addExposures;
	const removeExposures = updateObject.removeExposures;

	const routeMatch = useRouteMatch(
		Object.keys(QUOTE_ROUTES).map(
			(key) => QUOTE_ROUTES[key as keyof QuoteRoutes],
		),
	);
	const params = routeMatch?.params || {};
	const routeName = routeMatch?.pattern?.path || '';
	const allExposures = getActiveExposuresForSidebar(
		exposures,
		addExposures,
		removeExposures,
	);
	const policyConfig = productConfig.policyConfiguration;
	const policyLink = `/policy/${policy.locator}`;
	const baseLink = selectedQuote
		? `${policyLink}/quotes/${selectedQuote.locator}`
		: policyLink;
	const detailsLink = `${baseLink}/details`;
	const exposuresLink = `${baseLink}/exposures`;
	const policyDetailErrors =
		!!currentErrorObject?.data?.policyFieldErrors?.policyFieldErrors?.hasErrors;
	const exposureErrors =
		!!currentErrorObject?.data?.policyFieldErrors?.exposureValidationErrors
			?.length;
	const isPolicyValid = validatePolicyDetails(
		policy,
		policyConfig,
		`${Date.now()}`,
	).valid;

	const getItems = () => {
		const isDetailsSelected =
			!!params.quoteId &&
			!params.exposureId &&
			(routeName === QUOTE_ROUTES.POLICY_DETAILS ||
				routeName === QUOTE_ROUTES.AUX_DATA);

		let items: TreeItemData = {
			'Policy Details': {
				id: 'Policy Details',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Policy Details',
					link: detailsLink,
					selected: isDetailsSelected,
					hasError: policyDetailErrors || !isPolicyValid,
				},
			},
		};

		const exposureListItem: TreeItemInterface = {
			id: 'Exposures',
			children: [],
			hasChildren: !!exposures.length,
			isExpanded: expandedItems.includes('Exposures'),
			isChildrenLoading: false,
			data: {
				title: `${t('exposure.plural.upper')}`,
				link: exposuresLink,
				selected: routeName === QUOTE_ROUTES.EXPOSURES,
				hasError: exposureErrors,
				className: 'exposures',
			},
		};

		// Exposures drawers
		const exposureAndPerilItems = allExposures.reduce(
			(exposureItems: TreeItemData, exposure) => {
				const exposureConfig = policyConfig.exposures.find(
					(ex) => ex.name === exposure.name,
				);
				const exposureLink = `${exposuresLink}/${exposure.displayId}`;
				const perilsLink = `${exposuresLink}/${exposure.displayId}/perils`;
				const locator = exposure.locator;
				let perils = exposure.perils || [];
				if (locator.split('-')[0] === 'added') {
					const addedExposureObj = getAddedExposureFromContext(
						updateObject,
						locator,
					);
					const addedExposurePerils = addedExposureObj.perils || [];
					perils = addedExposurePerils.map((p, i) =>
						transformPerilCreateReqToPeril(p, i),
					);
				} else {
					const updatedExposureObj = findUpdatedExposureInContext(
						updateObject,
						locator,
					);
					if (updatedExposureObj) {
						const removedPerils = updatedExposureObj.removePerils || [];
						const addedPerils = updatedExposureObj.addPerils || [];
						const addedPerilsAsPerils = addedPerils.map((p, i) =>
							transformPerilCreateReqToPeril(p, i),
						);
						const filteredPerils = perils.filter(
							(p) => !removedPerils.includes(p.locator),
						);
						perils = [...filteredPerils, ...addedPerilsAsPerils];
					}
				}

				// Perils drawer
				const perilTreeItems = perils.reduce(
					(perilItems: TreeItemData, peril) => {
						const perilLink = `${perilsLink}/${peril.displayId}`;
						const perilConfig = exposureConfig.perils.find(
							(p) => p.name === peril.name,
						);
						const latestActivePerilCharacteristic =
							sortCharacteristicsByCoverageStartDate(
								getActiveCharacteristics(
									peril.characteristics,
								) as PerilCharacteristics[],
							).pop();
						const perilName =
							getEntityNameFrom_DescriptionField(
								latestActivePerilCharacteristic,
							) || perilConfig.displayName;
						const perilId = peril.locator;
						const isPerilPage = !!params.perilId;
						const isPerilIdSelected =
							isPerilPage && peril.displayId === params.perilId;
						const isPerilValid = validatePeril(
							peril,
							exposure,
							perilConfig,
							`${Date.now()}`,
						);

						perilItems[perilId] = {
							id: perilId,
							children: [],
							hasChildren: false,
							isExpanded: expandedItems.includes(perilId),
							isChildrenLoading: false,
							data: {
								title: perilName,
								link: perilLink,
								className: `peril-${perilId} peril`,
								selected: isPerilIdSelected,
								hasError: !isPerilValid,
							},
						};

						return perilItems;
					},
					{},
				);

				// Perils side button
				const perilTreeKeys = Object.keys(perilTreeItems);
				const exposureId = exposure.locator;
				const isExposurePage =
					!!params.exposureId &&
					!params.perilId &&
					routeName !== QUOTE_ROUTES.PERILS;
				const isExposureIdSelected =
					isExposurePage && exposure.displayId === params.exposureId;

				const isExposureValid = validateExposure(
					exposure,
					exposureConfig,
					`${Date.now()}`,
				).valid;
				const latestActiveExposureCharacteristic =
					sortCharacteristicsByStartDate(
						getActiveCharacteristics(
							exposure.characteristics,
						) as ExposureCharacteristics[],
					).pop();
				const exposureDisplayName = exposureConfig.displayName
					? exposureConfig.displayName
					: exposure.name;
				const exposureName =
					getEntityNameFrom_DescriptionField(
						latestActiveExposureCharacteristic,
					) || exposureDisplayName;

				const exposureItem: TreeItemInterface = {
					id: exposureId,
					children: [],
					hasChildren: true,
					isExpanded: expandedItems.includes(exposureId),
					isChildrenLoading: false,
					data: {
						title: exposureName,
						link: exposureLink,
						className: `exposure-${exposureId} exposure`,
						selected: isExposureIdSelected,
						hasError: !isExposureValid,
					},
				};

				const perilItemId = `${exposureId}-Perils`;
				const perilItem: TreeItemInterface = {
					id: perilItemId,
					children: perilTreeKeys,
					hasChildren: !!perils.length,
					isExpanded: expandedItems.includes(perilItemId),
					isChildrenLoading: false,
					data: {
						title: `${t('peril.plural.upper')}`,
						link: perilsLink,
						className: 'perils',
						selected:
							exposure.displayId === params.exposureId &&
							routeName === QUOTE_ROUTES.PERILS,
					},
				};
				perilTreeItems[perilItemId] = perilItem;
				exposureItem.children.push(perilItemId);

				exposureItems[exposureId] = exposureItem;
				exposureListItem.children.push(exposureId);

				return { ...exposureItems, ...perilTreeItems };
			},
			{},
		);

		items = {
			...items,
			...exposureAndPerilItems,
			Exposures: exposureListItem,
		};

		return items;
	};

	return getItems();
};

export default QuotePolicyTree;
